import type { SetNonNullable } from "type-fest";

import { reactive, toValue } from "vue";

import type { LengthAwarePaginator, ToMaybeRefs } from "@solvari/utils";

import { toSearchParams } from "@solvari/utils";

import { ziggy } from "@/shared/libs/ziggy";
import { kyCacheTags } from "~/shared/composables/useCacheTags.ts";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

type BaseCompany = Omit<
  App.DTO.Customer.CompanyCardExtendedDTO,
  "isSupportedLocale"
>;
type CompanyCardCompany =
  | (BaseCompany & { isSupportedLocale: false })
  | (SetNonNullable<BaseCompany> & { isSupportedLocale: true });

function useGeoProductCategory(
  params: ToMaybeRefs<App.DTO.Geo.GeoProductCategoryRequestDTO>,
) {
  return {
    key: () => ["api.v3.main-site.geoProductCategory", reactive(params)],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v3.main-site.geoProductCategory"), {
          searchParams: toSearchParams(reactive(params)),
        })
        .json<App.DTO.Geo.GeoProductCategoryWithStatsDTO>();
    }),
    staleTime: Infinity,
  };
}

function useCompanyCards(
  params: ToMaybeRefs<App.DTO.Customer.UnifiedCompanyCardRequestDTO>,
) {
  return {
    key: () => ["api.v4.main-site.company-cards", reactive(params)],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v4.main-site.company-cards.index"), {
          searchParams: toSearchParams(reactive(params)),
        })
        .json<App.Data.Api.V4.PaginatedResponseData<CompanyCardCompany>>();
    }),
    staleTime: Infinity,
  };
}

function useCompanyCardsLocalSearchTop(
  params: ToMaybeRefs<App.DTO.Customer.CompanyCardsLocalSearchTopRequestDTO>,
) {
  return {
    key: () => [
      "api.v4.main-site.company-cards.local-search-top",
      reactive(params),
    ],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v4.main-site.company-cards.local-search-top"), {
          searchParams: toSearchParams(reactive(params)),
        })
        .json<App.Data.Api.V4.CollectionResponseData<CompanyCardCompany>>();
    }),
    staleTime: Infinity,
  };
}

function useLocalReviews(
  params: ToMaybeRefs<App.DTO.Geo.LocalReviewsRequestDTO & { page: number }>,
) {
  return {
    key: () => ["api.v3.main-site.localReviews", reactive(params)],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v3.main-site.localReviews"), {
          // Body follows app.locale: anonymous author string + productLink resolution.
          // eslint-disable-next-line @typescript-eslint/naming-convention
          headers: { "solvari-locale": toValue(params.localeCode) },
          searchParams: toSearchParams(reactive(params)),
        })
        .json<
          LengthAwarePaginator<App.DTO.Customer.Review.CustomerReviewDTO>
        >();
    }),
    staleTime: 10 * 60 * 1000,
  };
}

function useLocalReviewStats(
  params: ToMaybeRefs<App.DTO.Geo.LocalReviewsRequestDTO>,
) {
  return {
    key: () => ["api.v3.main-site.localReviewStats", reactive(params)],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v3.main-site.localReviewStats"), {
          searchParams: toSearchParams(reactive(params)),
        })
        .json<App.DTO.Geo.LocalReviewStatsDTO>();
    }),
    staleTime: 10 * 60 * 1000,
  };
}

function useProductReviews(
  params: ToMaybeRefs<App.DTO.Geo.ProductReviewsRequestDTO & { page: number }>,
) {
  return {
    key: () => ["api.v3.main-site.productReviews", reactive(params)],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v3.main-site.productReviews"), {
          // app.locale body, same as useLocalReviews.
          // eslint-disable-next-line @typescript-eslint/naming-convention
          headers: { "solvari-locale": toValue(params.localeCode) },
          searchParams: toSearchParams(reactive(params)),
        })
        .json<
          LengthAwarePaginator<App.DTO.Customer.Review.CustomerReviewDTO>
        >();
    }),
    staleTime: 10 * 60 * 1000,
  };
}

export type { CompanyCardCompany };
export {
  useCompanyCards,
  useCompanyCardsLocalSearchTop,
  useGeoProductCategory,
  useLocalReviews,
  useLocalReviewStats,
  useProductReviews,
};
